(() => {
  'use strict';
  if (customElements.get('related-products-tabs')) return;
  class RelatedProductsTabs extends HTMLElement {
    connectedCallback() {
      this.generation = (this.generation || 0) + 1;
      this.tabs = Array.from(this.querySelectorAll('[data-rp-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-rp-panel]'));
      this.states = this.states || new Map();
      this.requests = this.requests || new Map();
      this.states.forEach((state, panel) => { if (!this.panels.includes(panel)) this.states.delete(panel); });
      this.activeIndex = this.panels.findIndex((panel) => !panel.hidden);
      if (this.activeIndex < 0) this.activeIndex = 0;
      this.panels.forEach((panel) => { if (!this.states.has(panel)) this.states.set(panel, { status: 'idle' }); });
      this.onClick = (event) => {
        const tab = event.target.closest('[data-rp-tab]');
        if (tab && this.contains(tab)) this.select(Number(tab.dataset.index));
        const retry = event.target.closest('[data-rp-retry]');
        if (retry && this.contains(retry)) this.load(this.panels.indexOf(retry.closest('[data-rp-panel]')), event.detail === 0);
      };
      this.onKey = (event) => {
        const current = event.target.closest('[data-rp-tab]');
        if (!current || !this.contains(current)) return;
        const index = this.tabs.indexOf(current);
        const direction = getComputedStyle(this).direction === 'rtl' ? -1 : 1;
        let target;
        if (event.key === 'ArrowRight') target = (index + direction + this.tabs.length) % this.tabs.length;
        if (event.key === 'ArrowLeft') target = (index - direction + this.tabs.length) % this.tabs.length;
        if (event.key === 'Home') target = 0;
        if (event.key === 'End') target = this.tabs.length - 1;
        if (target === undefined) return;
        event.preventDefault();
        this.tabs.forEach((tab, i) => { tab.tabIndex = i === target ? 0 : -1; });
        this.tabs[target].focus();
        this.keepTabVisible(this.tabs[target]);
      };
      this.onEditorSelect = (event) => {
        if (!event.detail || !event.detail.blockId) return;
        const index = this.tabs.findIndex((tab) => tab.dataset.blockId === event.detail.blockId);
        if (index >= 0) this.select(index);
      };
      this.onSectionSelect = (event) => { if (event.target.contains(this)) this.load(this.activeIndex); };
      this.onSectionLoad = (event) => { if (event.target.contains(this)) this.keepTabVisible(this.tabs[this.activeIndex]); };
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKey);
      document.addEventListener('shopify:block:select', this.onEditorSelect);
      document.addEventListener('shopify:section:select', this.onSectionSelect);
      document.addEventListener('shopify:section:load', this.onSectionLoad);
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.observer.disconnect();
          this.load(this.activeIndex);
        }, { rootMargin: '250px 0px' });
        this.observer.observe(this);
      } else { this.load(this.activeIndex); }
    }
    disconnectedCallback() {
      this.generation += 1;
      if (this.observer) this.observer.disconnect();
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKey);
      document.removeEventListener('shopify:block:select', this.onEditorSelect);
      document.removeEventListener('shopify:section:select', this.onSectionSelect);
      document.removeEventListener('shopify:section:load', this.onSectionLoad);
      this.requests.forEach((request, url) => {
        if (request.cancel) { request.cancel(); this.requests.delete(url); }
      });
      this.states.forEach((state) => {
        if (state.status === 'loading') state.status = 'idle';
      });
    }
    keepTabVisible(tab) {
      if (!tab) return;
      const list = tab.parentElement;
      const bounds = list.getBoundingClientRect();
      const rect = tab.getBoundingClientRect();
      const delta = rect.left < bounds.left ? rect.left - bounds.left : rect.right > bounds.right ? rect.right - bounds.right : 0;
      if (delta) list.scrollLeft += delta;
    }
    select(index) {
      if (!this.panels[index]) return;
      if (this.observer) this.observer.disconnect();
      const old = this.panels[this.activeIndex];
      if (index !== this.activeIndex && old) {
        const state = this.states.get(old);
        state.focusAfterLoad = false;
        const content = old.querySelector('[data-rp-content]');
        if (content.firstChild) {
          const carousel = content.querySelector('fc-pro');
          const engine = carousel && carousel.panels && carousel.panels[0] && carousel.panels[0].embla;
          if (engine) state.snap = engine.selectedScrollSnap();
          if (carousel && carousel.panels && typeof carousel.destroyCarousel === 'function') carousel.panels.forEach((panel) => carousel.destroyCarousel(panel));
          state.fragment = document.createDocumentFragment();
          while (content.firstChild) state.fragment.appendChild(content.firstChild);
        }
      }
      this.activeIndex = index;
      this.panels.forEach((panel, i) => { panel.hidden = i !== index; });
      this.tabs.forEach((tab, i) => { tab.setAttribute('aria-selected', String(i === index)); tab.tabIndex = i === index ? 0 : -1; });
      if (this.tabs[index]) this.keepTabVisible(this.tabs[index]);
      this.load(index);
    }
    status(panel, message, retry, loading = false) {
      const text = panel.querySelector('[data-rp-status]');
      text.textContent = message;
      text.hidden = !message;
      panel.querySelector('[data-rp-retry]').hidden = !retry;
      panel.setAttribute('aria-busy', String(loading));
    }
    skeleton(panel, visible) {
      let skeleton = panel.querySelector('[data-rp-skeleton]');
      if (!skeleton && visible) {
        const original = this.querySelector('[data-rp-skeleton]');
        if (original) { skeleton = original.cloneNode(true); panel.insertBefore(skeleton, panel.querySelector('[data-rp-content]')); }
      }
      if (skeleton) skeleton.hidden = !visible;
    }
    mount(panel, state) {
      if (panel.hidden) return;
      this.skeleton(panel, false);
      this.status(panel, state.empty ? this.dataset.empty : '', false);
      if (state.empty && this.tabs.length === 0) this.hidden = true;
      if (state.fragment) {
        const fragment = state.fragment;
        state.fragment = null;
        const carousel = fragment.querySelector('fc-pro');
        if (carousel) {
          ['layoutDesktop', 'layoutMobile', 'loop', 'scrollBy', 'sectionId'].forEach((key) => { carousel.dataset[key] = this.dataset[key]; });
          if (!customElements.get('fc-pro')) carousel.querySelector('.fc-pro__panel').classList.add('is-grid');
        }
        const animated = !state.rendered && this.dataset.animate === 'true' && 'IntersectionObserver' in window && typeof window.initializeScrollAnimationTrigger === 'function';
        if (animated) fragment.querySelectorAll('[data-rp-reveal]').forEach((element, i) => {
          element.classList.add('scroll-trigger', 'animate--slide-in', 'scroll-trigger--offscreen');
          element.setAttribute('data-cascade', '');
          element.style.setProperty('--animation-order', i + 1);
        });
        panel.querySelector('[data-rp-content]').appendChild(fragment);
        state.rendered = true;
        if (animated) {
          try { window.initializeScrollAnimationTrigger(panel, Boolean(window.Shopify && window.Shopify.designMode)); }
          catch (error) { panel.querySelectorAll('[data-rp-reveal]').forEach((element) => element.classList.remove('scroll-trigger', 'scroll-trigger--offscreen')); }
        }
        const engine = carousel && carousel.panels && carousel.panels[0] && carousel.panels[0].embla;
        if (engine && state.snap !== undefined) engine.scrollTo(state.snap, true);
        panel.dispatchEvent(new CustomEvent('related-products:loaded', { bubbles: true }));
      } else {
        const carousel = panel.querySelector('fc-pro');
        if (carousel && typeof carousel.activatePanel === 'function') carousel.activatePanel(0);
      }
      if (state.focusAfterLoad) {
        state.focusAfterLoad = false;
        if (!panel.hasAttribute('tabindex')) panel.tabIndex = -1;
        panel.focus({ preventScroll: true });
      }
    }
    getPayload(url) {
      if (this.requests.has(url)) return this.requests.get(url).promise;
      const record = {};
      this.requests.set(url, record);
      record.promise = (async () => {
        let timeout;
        const controller = 'AbortController' in window ? new AbortController() : null;
        try {
          const request = (async () => {
            const response = await fetch(url, { credentials: 'same-origin', signal: controller ? controller.signal : undefined });
            if (!response.ok) throw new Error('HTTP ' + response.status);
            return response.text();
          })();
          const deadline = new Promise((resolve, reject) => {
            record.cancel = () => { if (controller) controller.abort(); reject(new Error('Cancelled')); };
            timeout = setTimeout(record.cancel, 15000);
          });
          const html = await Promise.race([request, deadline]);
          const parser = document.createElement('template');
          parser.innerHTML = html;
          const source = parser.content.querySelector('template[data-rp-source]');
          if (!source) throw new Error('Missing product payload');
          const fragment = source.content.cloneNode(true);
          const limit = Math.min(10, Math.max(2, Number(this.dataset.limit) || 4));
          Array.from(fragment.querySelectorAll('[data-slide]')).slice(limit).forEach((slide) => slide.remove());
          return fragment;
        } catch (error) {
          if (this.requests.get(url) === record) this.requests.delete(url);
          throw error;
        } finally {
          clearTimeout(timeout);
          record.cancel = null;
        }
      })();
      return record.promise;
    }
    async load(index, focusAfterLoad = false) {
      const panel = this.panels[index];
      if (!panel || panel.hidden) return;
      const state = this.states.get(panel);
      if (state.status === 'ready') { this.mount(panel, state); return; }
      if (state.status === 'loading') return;
      const generation = this.generation;
      state.status = 'loading';
      state.focusAfterLoad = focusAfterLoad;
      this.status(panel, this.dataset.loading, false, true);
      this.skeleton(panel, true);
      try {
        const url = new URL(panel.dataset.url, window.location.origin);
        if (url.origin !== window.location.origin) throw new Error('Invalid collection URL');
        const payload = await this.getPayload(url.href);
        if (!this.isConnected || generation !== this.generation) return;
        const fragment = payload.cloneNode(true);
        state.empty = fragment.querySelectorAll('[data-slide]').length === 0;
        state.fragment = state.empty ? null : fragment;
        state.status = 'ready';
        this.mount(panel, state);
      } catch (error) {
        if (!this.isConnected || generation !== this.generation) return;
        state.status = 'error';
        this.skeleton(panel, false);
        this.status(panel, this.dataset.error, true);
        if (state.focusAfterLoad && !panel.hidden) panel.querySelector('[data-rp-retry]').focus({ preventScroll: true });
      }
    }
  }
  customElements.define('related-products-tabs', RelatedProductsTabs);
})();
